export const isOutOfPlatformBookedUser = (user) => Boolean(user?.outOfPlatform);

export const isPaidBookedUser = (user) => {
  if (user?.refundedAt) return false;
  if (isOutOfPlatformBookedUser(user)) return true;
  return String(user?.status ?? '').toLowerCase() === 'paid';
};

const countPaidBookings = (bookedUsers = []) => bookedUsers.filter(isPaidBookedUser).length;

const pickNonNegativeCount = (value) => {
  if (value == null || value === '') {
    return 0;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return 0;
  }

  return Math.max(0, Math.round(parsed));
};

const resolvePaidTickets = (event, bookedUsers) => {
  const paidFromBookings = countPaidBookings(bookedUsers);

  if (event?.paidSeats != null) {
    return Math.max(pickNonNegativeCount(event.paidSeats), paidFromBookings);
  }

  return paidFromBookings;
};

/** Whether attendance was finalized on the event details API. */
export const isAttendanceFinalized = (event = {}) => Boolean(event?.isFinalized);

/** Default form values from GET event detail (`actualAttendance`, `noShows`, etc.). */
export const buildAttendanceFinalizeDefaults = (event = {}) => {
  const bookedUsers = Array.isArray(event?.bookedUsers) ? event.bookedUsers : [];
  const totalRsvps = pickNonNegativeCount(event?.rsvpSeats ?? bookedUsers.length);
  const paidTickets = resolvePaidTickets(event, bookedUsers);

  return {
    totalRsvps,
    paidTickets,
    actualAttendance: pickNonNegativeCount(event?.actualAttendance),
    walkIns: pickNonNegativeCount(event?.walkIns),
    noShows: pickNonNegativeCount(event?.noShows),
    operationalNotes: String(event?.operationalNotes ?? '').trim(),
    isFinalized: isAttendanceFinalized(event),
  };
};

export const computeAttendancePreview = ({
  totalRsvps = 0,
  paidTickets = 0,
  actualAttendance = 0,
  noShows = 0,
}) => {
  const actual = Math.max(0, Number(actualAttendance) || 0);
  const paid = Math.max(0, Number(paidTickets) || 0);
  const rsvps = Math.max(0, Number(totalRsvps) || 0);
  const noShowCount = Math.max(0, Number(noShows) || 0);

  const paidAttendeesWhoShowedUp = Math.max(0, paid - noShowCount);
  const paidAttendeesWhoShowedUpPct = paid > 0 ? (paidAttendeesWhoShowedUp / paid) * 100 : 0;
  const noShowRate = paid > 0 ? (noShowCount / paid) * 100 : 0;
  const overallAttendanceRate = rsvps > 0 ? (actual / rsvps) * 100 : 0;

  return {
    paidAttendeesWhoShowedUp,
    paidAttendeesWhoShowedUpPct,
    noShowRate,
    overallAttendanceRate,
  };
};

const formatPercent = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0%';
  return `${num.toFixed(1)}%`;
};

export const formatAttendancePreviewLines = (preview) => ({
  paidAttendeesLine: `${preview.paidAttendeesWhoShowedUp} (${formatPercent(preview.paidAttendeesWhoShowedUpPct)})`,
  noShowRateLine: formatPercent(preview.noShowRate),
  overallAttendanceLine: `${formatPercent(preview.overallAttendanceRate)} of RSVPs`,
});

export const buildFinalizeAttendancePayload = (form) => ({
  actualAttendance: Math.max(0, Number(form.actualAttendance) || 0),
  walkIns: Math.max(0, Number(form.walkIns) || 0),
  noShows: Math.max(0, Number(form.noShows) || 0),
  operationalNotes: String(form.operationalNotes ?? '').trim(),
});

export const validateAttendanceFinalizeForm = (form) => {
  const actual = Number(form.actualAttendance);
  const walk = Number(form.walkIns);
  const noShow = Number(form.noShows);

  if (!Number.isFinite(actual) || actual < 0) {
    return 'Actual attendance must be a non-negative number.';
  }
  if (!Number.isFinite(walk) || walk < 0) {
    return 'Walk-ins must be a non-negative number.';
  }
  if (!Number.isFinite(noShow) || noShow < 0) {
    return 'No-shows must be a non-negative number.';
  }
  if (noShow > Number(form.paidTickets)) {
    return 'No-shows cannot exceed paid tickets.';
  }
  if (walk > actual) {
    return 'Walk-ins cannot exceed actual attendance.';
  }
  return null;
};
